import { FC } from 'react'
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { FormularioDinamico } from '@/components/formulario/FormularioDinamico'
import { UseFormularioReserva } from '@/hooks/useFormularioReserva'
import { mensagemErroFormulario } from '@/services/formularioService'

/** Texto do passo sem formulário (AN-6 §2) — também usado no stepper. */
export const SEM_FORMULARIO_ESTRUTURACAO = 'Este produto não solicita dados nesta etapa'

interface Props {
  /** Estado do formulário ESTRUTURACAO, mantido pela página (o stepper também o lê). */
  form: UseFormularioReserva
}

/**
 * Etapa 2 do workspace — "Dados do produto" (AN-6 §7): o formulário de ESTRUTURACAO do
 * produto, pela versão vinculada à reserva (ou a vigente, fixada no 1º salvamento). Nada
 * fixo: sem formulário, a etapa só avisa. As pendências são as do servidor (até o
 * encaminhamento ao parceiro, que é onde este formulário passa a ser exigido).
 */
export const EtapaFormularioProduto: FC<Props> = ({ form }) => {
  const { enqueueSnackbar } = useSnackbar()

  if (form.estado === 'carregando' || form.estado === 'inativo') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label="Carregando os dados do produto" />
      </Box>
    )
  }
  if (form.estado === 'erro') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void form.recarregar()}>
            Tentar novamente
          </Button>
        }
      >
        Não foi possível carregar os dados solicitados pelo produto.
      </Alert>
    )
  }
  if (!form.temFormulario || !form.formulario?.schema) {
    return (
      <Typography variant="body2" color="text.secondary">
        {SEM_FORMULARIO_ESTRUTURACAO}.
      </Typography>
    )
  }

  const { formulario } = form
  const editavel = formulario.editavel
  const pendencias = form.sujo ? [] : formulario.pendencias ?? []

  const salvar = async () => {
    try {
      await form.salvar()
      enqueueSnackbar('Dados do produto salvos', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(mensagemErroFormulario(e, 'Não foi possível salvar os dados do produto'), {
        variant: 'error',
      })
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dados solicitados pelo produto para a estruturação
        {formulario.versao != null && formulario.versao > 0 ? ` (versão ${formulario.versao})` : ''}.
      </Typography>

      {!editavel && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fora da janela de edição desta etapa — os dados ficam só para leitura.
        </Alert>
      )}

      {pendencias.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Ainda falta</AlertTitle>
          {pendencias.map((p, i) => (
            <Typography key={i} variant="body2">
              • {p.mensagem}
            </Typography>
          ))}
        </Alert>
      )}

      <FormularioDinamico
        schema={formulario.schema as NonNullable<typeof formulario.schema>}
        respostas={form.respostas}
        pendencias={pendencias}
        somenteLeitura={!editavel || form.salvando}
        onChange={form.alterar}
      />

      {editavel && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => void salvar()}
            disabled={form.salvando || !form.sujo}
            startIcon={form.salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Salvar dados do produto
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export default EtapaFormularioProduto
